import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AlertsService } from "src/app/core/alerts.service";
import { ApiError } from "src/app/core/api-error";
import { UserService } from "src/app/core/auth";
import { environment } from "src/environments";

@Injectable()
export class NetworkResponseErrorInterceptor implements HttpInterceptor {
  public constructor(
    private readonly user: UserService,
    private readonly alerts: AlertsService,
    private readonly router: Router,
  ) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error) => {
        const isHttpErrorInstance = error instanceof HttpErrorResponse;

        const isHttpError =
          isHttpErrorInstance &&
          typeof error.error === "object" &&
          error.error !== null &&
          "errors" in error.error &&
          "message" in error.error;

        if (!isHttpErrorInstance || isHttpError) {
          return throwError(() => error);
        }

        const apiError = ApiError.create(error);

        if (apiError.status === 401) {
          // Logout whenever the request is to one of the authenticated APIs
          // that is provided an auth token from the UI via MSAL.

          if (request.url.startsWith(environment.apiUrls.mr.href)) {
            this.user.logout();
            return EMPTY;
          }

          return throwError(() => apiError);
        }

        if (this.redirectIfInvalidIdRoute(apiError, request)) {
          return throwError(() => apiError);
        }

        // Skip the alert for "Not Found" errors so that an appropriate splash
        // page can be shown to the user as needed.
        if (apiError.status === 404) {
          return throwError(() => apiError);
        }

        // Only alert on GETs so that other methods can handle their own errors
        // as needed.
        if (request.method === "GET") {
          // Check if the request wants to suppress error alerts
          const suppressAlert =
            request.headers.get("X-Suppress-Error-Alert") === "true";

          const message =
            apiError?.combinedDetailsMessage ??
            "The page failed to load correctly. Please refresh to try again.";

          if (!suppressAlert) {
            this.alerts.error({
              title: `Request Error (${apiError.status})`,
              message,
              error,
            });
          }
        }

        return throwError(() => apiError);
      }),
    );
  }

  /**
   * Handles the scenario where a GET request returns a 400 error (Bad Request) or 404 (Not Found)
   * and the current route looks like a "details" page (e.g., `/purchase-orders/123`).
   *
   * If the route ends with a numeric ID,
   * this method redirects the user back to the parent "list" route
   * (e.g., `/purchase-orders`).
   *
   * @param apiError - The API error object created from the HTTP error.
   * @param request - The original HTTP request that triggered the error.
   * @returns true if a redirect was triggered, otherwise false.
   */
  private redirectIfInvalidIdRoute(
    apiError: ApiError,
    request: HttpRequest<unknown>,
  ): boolean {
    // Only handle GET requests that returned a 400 Bad Request or 404 Not Found responses.
    // Any other status or method should be ignored.
    if (
      (apiError.status === 400 || apiError.status === 404) &&
      request.method === "GET"
    ) {
      // Get the current URL (e.g., /purchase-orders/123?tab=details)
      // and remove any query parameters (?tab=details → ignored).
      const currentUrl = this.router.url.split("?")[0];

      // Split the URL into its path segments:
      // e.g. "/purchase-orders/123" → ["purchase-orders", "123"]
      const segments = currentUrl.split("/").filter(Boolean);

      // Get the last segment of the path (in the example, "123").
      const last = segments.at(-1);

      // Return false If:
      // 1. The last segment is undifend or not a number
      // 2. There’s no parent segment before it (so it’s just "/123")
      if (!last || !/^\d+$/.test(last) || segments.length < 2) {
        return false;
      }

      // Remove the trailing "/123" part to get the parent route
      const listUrl = currentUrl.substring(0, currentUrl.lastIndexOf("/"));

      this.router.navigateByUrl(listUrl).catch(() => null);
      return true;
    }

    return false;
  }
}
